import { MobileOfferCapabilities, MobileOfferEnvironment, MobileOfferProvider, MobileOfferPurpose, MobileOfferTerms } from '@numeo/types'
import { MobilePlan } from '@numeo/types-core'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { mobileOfferError, mobileOffersApi } from './api'
import { CountryPicker, availableOfferCountries } from './CountryPicker'
import { OfferButton, offerFieldClass, OfferNotice, OfferSection } from './ui'
import { offerPlans, planLabels, storeLabels } from './offerLabels'

const schema = yup.object({
    plan: yup.mixed<MobilePlan>().oneOf(offerPlans).required(),
    environment: yup.mixed<MobileOfferEnvironment>().oneOf(['sandbox', 'production']).required(),
    percentOff: yup.number().typeError('Enter a discount percentage').integer().min(1).max(99).required(),
    discountedPeriods: yup.number().typeError('Enter the number of monthly payments').integer().min(1).max(12).required(),
    countries: yup.array(yup.string().required()).min(1, 'Choose at least one available country').max(200).required(),
    eligibility: yup.mixed<'all' | 'churned'>().oneOf(['all', 'churned']).required(),
    providers: yup
        .array(yup.mixed<MobileOfferProvider>().oneOf(['google', 'apple']).required())
        .min(1, 'Choose at least one store')
        .required(),
})
type Values = yup.InferType<typeof schema>

export function CreateOfferForm({
    onContinue,
    initialTerms,
    initialProviders,
    retentionPlan,
}: {
    onContinue: (terms: MobileOfferTerms, providers: MobileOfferProvider[]) => void
    initialTerms?: MobileOfferTerms
    initialProviders?: MobileOfferProvider[]
    /** Set → a cancellation-retention offer for this plan only: plan is locked, audience is every subscriber. */
    retentionPlan?: MobilePlan
}) {
    const [capabilities, setCapabilities] = useState<MobileOfferCapabilities | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [reload, setReload] = useState(0)
    const {
        register,
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<Values>({
        resolver: yupResolver(schema),
        defaultValues: {
            plan: MobilePlan.ULTRA_MOBILE,
            environment: 'sandbox',
            percentOff: 30,
            discountedPeriods: 3,
            countries: [],
            ...initialTerms,
            ...(retentionPlan ? { plan: retentionPlan, percentOff: initialTerms?.percentOff ?? 50 } : {}),
            eligibility: initialTerms?.customerEligibilities?.length === 1 && !retentionPlan ? 'churned' : 'all',
            providers: initialProviders || ['google'],
        },
    })
    const plan = watch('plan')
    const providers = watch('providers')
    const countries = watch('countries')
    useEffect(() => {
        let active = true
        setLoading(true)
        setCapabilities(null)
        setError('')
        mobileOffersApi
            .capabilities(plan, retentionPlan ? MobileOfferPurpose.CANCELLATION_RETENTION : undefined)
            .then((result) => {
                if (active) setCapabilities(result)
            })
            .catch((failure) => {
                if (active) setError(mobileOfferError(failure))
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [plan, reload, retentionPlan])
    const current = capabilities?.plan === plan ? capabilities : null
    const available = new Set(availableOfferCountries(current, providers))
    const supported = !loading && !!current && providers.length > 0 && countries.length > 0 && countries.every((country) => available.has(country))
    const submit = handleSubmit(({ providers: selected, eligibility, ...values }) => {
        if (!supported) return
        onContinue(
            {
                ...values,
                cycle: 'monthly',
                customerEligibilities: eligibility === 'churned' ? ['EXPIRED'] : ['NEW', 'EXISTING', 'EXPIRED'],
                ...(retentionPlan ? { plan: retentionPlan, purpose: MobileOfferPurpose.CANCELLATION_RETENTION } : {}),
            },
            selected
        )
    })
    return (
        <OfferSection
            title={retentionPlan ? `${planLabels[retentionPlan]} retention offer` : 'Offer details'}
            description={
                retentionPlan
                    ? 'Shown only to subscribers on this plan when they try to cancel. It is never assigned to customers.'
                    : 'Set the terms once, then choose where to create this offer.'
            }
        >
            <form onSubmit={submit} className="space-y-6">
                <fieldset className="space-y-3">
                    <legend className="mb-2 text-sm font-medium">Stores</legend>
                    <Controller
                        name="providers"
                        control={control}
                        render={({ field }) => (
                            <div className="grid gap-3 sm:grid-cols-2">
                                {(['google', 'apple'] as const).map((provider) => (
                                    <label
                                        key={provider}
                                        className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 ${field.value.includes(provider) ? 'border-blue-600 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700'}`}
                                    >
                                        <input
                                            type="checkbox"
                                            aria-label={storeLabels[provider]}
                                            className="mt-1 h-4 w-4 accent-blue-600"
                                            checked={field.value.includes(provider)}
                                            onChange={(event) =>
                                                field.onChange(
                                                    event.target.checked ? [...field.value, provider] : field.value.filter((item) => item !== provider)
                                                )
                                            }
                                        />
                                        <span>
                                            <span className="block text-sm font-semibold">{storeLabels[provider]}</span>
                                            <span className="text-xs text-slate-500 dark:text-slate-400">
                                                {provider === 'google' ? 'In-app subscription offer' : 'One-time redemption codes'}
                                            </span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                    />
                </fieldset>
                <div className="grid gap-4 sm:grid-cols-2">
                    {!retentionPlan && (
                        <label className="space-y-1 text-sm font-medium">
                            Plan
                            <select className={offerFieldClass} {...register('plan')}>
                                {offerPlans.map((value) => (
                                    <option key={value} value={value}>
                                        {planLabels[value]}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                    <label className="space-y-1 text-sm font-medium">
                        Purchase mode
                        <select className={offerFieldClass} {...register('environment')}>
                            <option value="sandbox">Test purchases</option>
                            <option value="production">Real purchases</option>
                        </select>
                    </label>
                    <label className="space-y-1 text-sm font-medium">
                        Requested discount (%)
                        <input className={offerFieldClass} type="number" min={1} max={99} {...register('percentOff')} />
                    </label>
                    <label className="space-y-1 text-sm font-medium">
                        Discounted monthly payments
                        <input className={offerFieldClass} type="number" min={1} max={12} {...register('discountedPeriods')} />
                        <span className="block text-xs font-normal text-slate-500 dark:text-slate-400">
                            Includes the first payment, then renews at the regular price.
                        </span>
                    </label>
                    {!retentionPlan && (
                        <label className="space-y-1 text-sm font-medium sm:col-span-2">
                            Customer audience
                            <select className={offerFieldClass} {...register('eligibility')}>
                                <option value="all">All subscriber categories</option>
                                <option value="churned">Expired subscribers</option>
                            </select>
                        </label>
                    )}
                </div>
                <Controller
                    control={control}
                    name="countries"
                    render={({ field }) => (
                        <CountryPicker value={field.value} onChange={field.onChange} capabilities={current} providers={providers} loading={loading} />
                    )}
                />
                {providers.map(
                    (provider) =>
                        current &&
                        (!current.stores[provider].configured || current.stores[provider].error) && (
                            <OfferNotice key={provider} error>
                                {storeLabels[provider]}:{' '}
                                {current.stores[provider].error || 'This plan is not configured. Ask engineering to configure this store, or deselect it.'}
                            </OfferNotice>
                        )
                )}
                {Object.entries(errors).map(([field, detail]) => (
                    <OfferNotice error key={field}>
                        {detail.message}
                    </OfferNotice>
                ))}
                {error && <OfferNotice error>{error}</OfferNotice>}
                {(error || providers.some((provider) => current?.stores[provider].error)) && (
                    <OfferButton variant="secondary" onClick={() => setReload((value) => value + 1)}>
                        Retry country availability
                    </OfferButton>
                )}
                <div className="rounded-lg bg-slate-50 p-3 text-xs leading-relaxed text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                    Test purchases do not mean a separate store catalog. Creating an offer changes live store configuration. Nothing is created in a store until
                    you review prices and confirm.
                </div>
                <div className="flex justify-end border-t border-slate-200 pt-4 dark:border-slate-700">
                    <OfferButton type="submit" disabled={!supported}>
                        Save draft & preview prices
                    </OfferButton>
                </div>
            </form>
        </OfferSection>
    )
}
